import { useEffect, useRef, useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { marked } from "marked"
import { Archive, ArrowUp, Loader2, Plus, Sparkles, Wand2 } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Textarea } from "../../components/ui/textarea"
import { toast } from "../../components/ui/use-toast"
import {
  createSalesWizConversation,
  getSalesWizMessages,
  listSalesWizConversations,
  SalesWizConversation,
  SalesWizMessage,
  sendSalesWizMessageStream,
  updateSalesWizConversation,
} from "../../api/sales_wiz"

interface SalesWizPanelProps {
  /** Omit for a chain chat — a conversation about the shared retail chain catalog. */
  reportId?: string
  weeks?: number
  geography?: string
  brand?: string
}

/** Copy that differs between a report chat and a chain chat. Everything else is shared. */
export interface SalesWizPanelCopy {
  name: string
  emptyTitle: string
  blurb: string
  placeholder: string
  newChatTitle: string
  firstStatus: string
  examples: string[]
}

const REPORT_COPY: SalesWizPanelCopy = {
  name: "SalesWiz",
  emptyTitle: "Ask SalesWiz",
  blurb: "Analyze syndicated sales, distribution, velocity, promo, and SKU mix for this report.",
  placeholder: "Ask about growth drivers, share, velocity, distribution, or buyer talking points...",
  newChatTitle: "New SalesWiz chat",
  firstStatus: "Reading syndicated data...",
  examples: [
    "What SKUs should I lead with in a buyer meeting?",
    "Which competitors are taking share?",
    "Is growth from distribution or velocity?",
  ],
}

const CHAIN_COPY: SalesWizPanelCopy = {
  name: "Chains",
  emptyTitle: "Ask about chains",
  blurb: "How a retailer works: category reviews, ad and slotting fees, demo and merchandising policy, HQ and store count.",
  placeholder: "Ask how a chain runs category reviews, what its ad or slotting fees are, or who it reports to...",
  newChatTitle: "New chain chat",
  firstStatus: "Looking up the chain catalog...",
  examples: [
    "What is Sprouts' slotting policy?",
    "When does Kroger run category reviews, and can brands attend?",
    "Which chains allow shippers and in-store demos?",
  ],
}

export function panel_copy(reportId?: string): SalesWizPanelCopy {
  return reportId ? REPORT_COPY : CHAIN_COPY
}

marked.setOptions({
  breaks: true,
  gfm: true,
})

function SalesWizThinkingIcon(props: { className?: string }) {
  return (
    <span class={`sales-wiz-thinking-icon relative inline-flex h-5 w-5 items-center justify-center ${props.className || ""}`} aria-hidden="true">
      <Wand2 className="sales-wiz-wand h-4 w-4 text-plue-700" />
      <Sparkles className="sales-wiz-sparkle absolute -right-1 -top-1 h-3 w-3 text-amber-500" />
      <span class="sales-wiz-spark-dot absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-amber-300" />
    </span>
  )
}

function visible_example_prompts(examples: string[], hiddenPrompts: string[]): string[] {
  return examples.filter((prompt: string) => !hiddenPrompts.includes(prompt))
}

export function conversation_display_title(
  conversation: Pick<SalesWizConversation, "title">,
  fallback: string = REPORT_COPY.newChatTitle
): string {
  return conversation.title?.trim() || fallback
}

function conversation_timestamp(conversation: SalesWizConversation): string {
  return `Chat ${format(parseISO(conversation.updated_at), "MMM d, h:mm a")}`
}

export function escape_raw_html_for_sales_wiz_markdown(content: string): string {
  return content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
}

function render_message_content(message: SalesWizMessage) {
  if (message.role === "user") {
    return <div class="whitespace-pre-wrap break-words">{message.content}</div>
  }
  const html = marked.parse(escape_raw_html_for_sales_wiz_markdown(message.content)) as string
  return (
    <div
      class="sales-wiz-markdown break-words"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

interface SalesWizMessageRowProps {
  message: SalesWizMessage
}

function SalesWizMessageRow(props: SalesWizMessageRowProps) {
  const is_user = props.message.role === "user"
  return (
    <div class={`flex ${is_user ? "justify-end" : "justify-start"}`}>
      <div class={`max-w-[90%] text-sm leading-relaxed ${is_user ? "rounded-2xl bg-gray-100 px-4 py-2.5 text-gray-900" : "text-gray-800"}`}>
        {props.message.content ? render_message_content(props.message) : (
          <span class="inline-flex items-center gap-2 text-gray-500">
            <SalesWizThinkingIcon />
            Thinking...
          </span>
        )}
      </div>
    </div>
  )
}

interface SalesWizExampleChipProps {
  prompt: string
  onSelect: (prompt: string) => void
}

function SalesWizExampleChip(props: SalesWizExampleChipProps) {
  return (
    <button
      type="button"
      class="max-w-full rounded-full border border-gray-200 bg-white px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
      onClick={() => props.onSelect(props.prompt)}
    >
      {props.prompt}
    </button>
  )
}

export function SalesWizPanel(props: SalesWizPanelProps) {
  const [conversation, setConversation] = useState<SalesWizConversation | null>(null)
  const [conversations, setConversations] = useState<SalesWizConversation[]>([])
  const [messages, setMessages] = useState<SalesWizMessage[]>([])
  const [inputValue, setInputValue] = useState("")
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [streamingStatus, setStreamingStatus] = useState("")
  const [hiddenExamplePrompts, setHiddenExamplePrompts] = useState<string[]>([])
  const streamAbortRef = useRef<AbortController | null>(null)
  const copy = panel_copy(props.reportId)

  useEffect(() => {
    loadConversation()
    return () => {
      streamAbortRef.current?.abort()
      streamAbortRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.reportId])

  async function loadConversation() {
    setLoading(true)
    try {
      const existingConversations = await listSalesWizConversations(props.reportId)
      const nextConversation = existingConversations[0] ?? await createSalesWizConversation({ report_id: props.reportId })
      setConversations(existingConversations.length > 0 ? existingConversations : [nextConversation])
      await select_conversation(nextConversation)
    } catch (error) {
      console.error("Failed to load SalesWiz conversation:", error)
      toast({
        title: `${copy.name} unavailable`,
        description: error instanceof Error ? error.message : `Failed to load ${copy.name}`,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  function handlePromptClick(prompt: string) {
    setInputValue(prompt)
    setHiddenExamplePrompts((prev: string[]) => Array.from(new Set([...prev, prompt])))
  }

  async function select_conversation(nextConversation: SalesWizConversation) {
    setConversation(nextConversation)
    setHiddenExamplePrompts([])
    const nextMessages = await getSalesWizMessages(nextConversation.id)
    setMessages(nextMessages)
  }

  async function handle_new_chat() {
    if (sending) return
    setLoading(true)
    try {
      const nextConversation = await createSalesWizConversation({ report_id: props.reportId, force_new: true })
      setConversations((prev: SalesWizConversation[]) => [nextConversation, ...prev.filter((item: SalesWizConversation) => item.id !== nextConversation.id)])
      await select_conversation(nextConversation)
      setInputValue("")
      setStreamingStatus("")
    } catch (error) {
      console.error("Failed to create SalesWiz conversation:", error)
      toast({
        title: `${copy.name} unavailable`,
        description: error instanceof Error ? error.message : "Failed to start a new chat",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  async function handle_archive_chat(targetConversation: SalesWizConversation) {
    if (sending) return
    setLoading(true)
    try {
      await updateSalesWizConversation(targetConversation.id, { archived: true })
      const remainingConversations = conversations.filter((item: SalesWizConversation) => item.id !== targetConversation.id)
      setConversations(remainingConversations)
      if (targetConversation.id === conversation?.id) {
        if (remainingConversations.length > 0) {
          await select_conversation(remainingConversations[0])
        } else {
          const nextConversation = await createSalesWizConversation({ report_id: props.reportId, force_new: true })
          setConversations([nextConversation])
          await select_conversation(nextConversation)
        }
      }
    } catch (error) {
      console.error("Failed to archive SalesWiz conversation:", error)
      toast({
        title: "Could not archive chat",
        description: error instanceof Error ? error.message : "Failed to archive this SalesWiz chat",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault()
      handleSendMessage()
    }
  }

  async function handleSendMessage() {
    if (!conversation || !inputValue.trim() || sending) return

    const userContent = inputValue.trim()
    const assistantMessageId = `stream-${Date.now()}`
    const userMessage: SalesWizMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: conversation.id,
      role: "user",
      content: userContent,
      created_at: new Date().toISOString(),
    }
    const assistantMessage: SalesWizMessage = {
      id: assistantMessageId,
      conversation_id: conversation.id,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
    }

    setInputValue("")
    if (copy.examples.includes(userContent)) {
      setHiddenExamplePrompts((prev: string[]) => Array.from(new Set([...prev, userContent])))
    }
    setSending(true)
    setStreamingStatus(copy.firstStatus)
    setMessages((prev: SalesWizMessage[]) => [...prev, userMessage, assistantMessage])

    try {
      streamAbortRef.current?.abort()
      await new Promise<void>((resolve, reject) => {
        streamAbortRef.current = sendSalesWizMessageStream(
          conversation.id,
          {
            content: userContent,
            current_weeks: props.weeks,
            current_geography: props.geography,
            current_brand: props.brand,
          },
          {
            onThinking: (message: string) => setStreamingStatus(message),
            onToolCall: (toolName: string) => {
              const status = sales_wiz_tool_status(toolName)
              setStreamingStatus(status)
              setMessages((prev: SalesWizMessage[]) => prev.map((message: SalesWizMessage) => (
                message.id === assistantMessageId && !message.content
                  ? { ...message, content: `${status}...` }
                  : message
              )))
            },
            onTextDelta: (content: string) => {
              setStreamingStatus("Writing the answer...")
              setMessages((prev: SalesWizMessage[]) => prev.map((message: SalesWizMessage) => (
                message.id === assistantMessageId
                  ? { ...message, content: is_status_placeholder(message.content) ? content : message.content + content }
                  : message
              )))
            },
            onText: (content: string) => {
              setMessages((prev: SalesWizMessage[]) => prev.map((message: SalesWizMessage) => (
                message.id === assistantMessageId ? { ...message, content } : message
              )))
            },
            onDone: () => {
              setStreamingStatus("")
              resolve()
            },
            onError: (error: string) => reject(new Error(error)),
          }
        )
      })
      const nextMessages = await getSalesWizMessages(conversation.id)
      setMessages(nextMessages)
      const nextConversations = await listSalesWizConversations(props.reportId)
      setConversations(nextConversations)
      const refreshedConversation = nextConversations.find((item: SalesWizConversation) => item.id === conversation.id)
      if (refreshedConversation) setConversation(refreshedConversation)
    } catch (error) {
      console.error("Failed to send SalesWiz message:", error)
      setMessages((prev: SalesWizMessage[]) => prev.filter((message: SalesWizMessage) => message.id !== userMessage.id && message.id !== assistantMessageId))
      toast({
        title: `${copy.name} error`,
        description: error instanceof Error ? error.message : "Failed to send message",
        variant: "destructive",
      })
    } finally {
      setSending(false)
      setStreamingStatus("")
      streamAbortRef.current = null
    }
  }

  const examplePrompts = visible_example_prompts(copy.examples, hiddenExamplePrompts)
  const has_messages = messages.length > 0

  return (
    <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <style>{`
        @keyframes sales-wiz-wand-swish {
          0%, 100% { transform: rotate(-14deg) translateY(0); }
          35% { transform: rotate(10deg) translateY(-1px); }
          65% { transform: rotate(-4deg) translateY(1px); }
        }
        @keyframes sales-wiz-spark-pulse {
          0%, 100% { opacity: 0.35; transform: scale(0.72); }
          40% { opacity: 1; transform: scale(1.12); }
          70% { opacity: 0.7; transform: scale(0.9); }
        }
        @keyframes sales-wiz-spark-drift {
          0%, 100% { opacity: 0; transform: translate(0, 0) scale(0.8); }
          45% { opacity: 0.9; transform: translate(5px, -5px) scale(1); }
        }
        .sales-wiz-thinking-icon .sales-wiz-wand {
          transform-origin: 15% 85%;
          animation: sales-wiz-wand-swish 1.25s ease-in-out infinite;
        }
        .sales-wiz-thinking-icon .sales-wiz-sparkle {
          animation: sales-wiz-spark-pulse 1.25s ease-in-out infinite;
        }
        .sales-wiz-thinking-icon .sales-wiz-spark-dot {
          animation: sales-wiz-spark-drift 1.25s ease-in-out infinite;
        }
        .sales-wiz-markdown {
          line-height: 1.55;
        }
        .sales-wiz-markdown p {
          margin-bottom: 0.75rem;
        }
        .sales-wiz-markdown p:last-child {
          margin-bottom: 0;
        }
        .sales-wiz-markdown ul,
        .sales-wiz-markdown ol {
          margin: 0.5rem 0 0.75rem 1.25rem;
        }
        .sales-wiz-markdown li {
          margin-bottom: 0.25rem;
        }
        .sales-wiz-markdown strong {
          font-weight: 700;
          color: #111827;
        }
        .sales-wiz-markdown h1,
        .sales-wiz-markdown h2,
        .sales-wiz-markdown h3 {
          margin: 0.75rem 0 0.4rem;
          font-weight: 700;
          color: #111827;
        }
        .sales-wiz-markdown h1 { font-size: 1.05rem; }
        .sales-wiz-markdown h2 { font-size: 1rem; }
        .sales-wiz-markdown h3 { font-size: 0.95rem; }
      `}</style>
      <div class="flex min-h-[480px]">
        <aside class="hidden w-56 shrink-0 flex-col border-r border-gray-100 bg-gray-50/60 lg:flex">
          <div class="flex items-center justify-between px-3 py-3">
            <span class="text-xs font-medium uppercase tracking-wide text-gray-500">Chats</span>
            <Button variant="ghost" size="icon" onClick={handle_new_chat} disabled={loading || sending} aria-label="New chat">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div class="flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
            {conversations.length === 0 ? (
              <p class="px-2 py-1 text-sm text-gray-500">No chats yet</p>
            ) : (
              conversations.map((item: SalesWizConversation) => {
                const isActive = item.id === conversation?.id
                return (
                  <div
                    key={item.id}
                    class={`flex items-center gap-0.5 rounded-lg ${isActive ? "bg-white shadow-sm" : "hover:bg-white/80"}`}
                  >
                    <button
                      type="button"
                      class="min-w-0 flex-1 rounded-lg px-2.5 py-2 text-left disabled:opacity-50"
                      disabled={sending}
                      onClick={() => select_conversation(item)}
                    >
                      <span class="block truncate text-sm font-medium text-gray-900">
                        {conversation_display_title(item, copy.newChatTitle)}
                      </span>
                      <span class="mt-0.5 block truncate text-xs text-gray-500">
                        {conversation_timestamp(item)}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-8 w-8 shrink-0 text-gray-400 hover:text-red-600"
                      disabled={sending || loading}
                      onClick={() => handle_archive_chat(item)}
                      aria-label={`Archive ${conversation_display_title(item, copy.newChatTitle)}`}
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })
            )}
          </div>
        </aside>

        <div class="flex min-w-0 flex-1 flex-col">
          <div class="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 lg:hidden">
            <div class="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-plue-700" />
              <span class="text-sm font-medium text-gray-900">{copy.name}</span>
            </div>
            <div class="flex items-center gap-1">
              {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              <Button variant="ghost" size="sm" onClick={handle_new_chat} disabled={loading || sending}>
                <Plus className="mr-1.5 h-4 w-4" />
                New chat
              </Button>
            </div>
          </div>

          <div class="max-h-[min(56vh,520px)] flex-1 overflow-y-auto px-4 py-6 md:px-8">
            {loading && !has_messages ? (
              <div class="flex items-center justify-center py-20 text-sm text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : !has_messages ? (
              <div class="mx-auto flex max-w-2xl flex-col items-center justify-center py-12 text-center md:py-20">
                <span class="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-plue-50 text-plue-700">
                  {sending ? <SalesWizThinkingIcon /> : <Wand2 className="h-5 w-5" />}
                </span>
                <h2 class="text-xl font-medium tracking-tight text-gray-900">{copy.emptyTitle}</h2>
                <p class="mt-2 max-w-md text-sm leading-relaxed text-gray-500">{copy.blurb}</p>
                {examplePrompts.length > 0 && (
                  <div class="mt-10 flex w-full flex-wrap justify-center gap-2">
                    {examplePrompts.map((prompt: string) => (
                      <SalesWizExampleChip key={prompt} prompt={prompt} onSelect={handlePromptClick} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div class="mx-auto max-w-3xl space-y-8">
                {messages.map((message: SalesWizMessage) => (
                  <SalesWizMessageRow key={message.id} message={message} />
                ))}
              </div>
            )}
          </div>

          <div class="border-t border-gray-100 px-4 py-4 md:px-8">
            {streamingStatus && (
              <p class="mx-auto mb-2 flex max-w-3xl items-center gap-2 text-xs text-gray-500">
                <SalesWizThinkingIcon className="scale-90" />
                {streamingStatus}
              </p>
            )}
            <div class="relative mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white shadow-sm focus-within:border-gray-300 focus-within:ring-2 focus-within:ring-plue-100">
              <Textarea
                value={inputValue}
                onInput={(event: Event) => setInputValue((event.currentTarget as HTMLTextAreaElement).value)}
                onKeyDown={handleKeyDown}
                placeholder={copy.placeholder}
                className="min-h-[52px] resize-none border-0 bg-transparent px-4 pb-12 pt-3.5 shadow-none focus-visible:ring-0"
              />
              <Button
                size="icon"
                class="absolute bottom-2 right-2 h-9 w-9 rounded-full"
                onClick={handleSendMessage}
                disabled={sending || !conversation || !inputValue.trim()}
                aria-label={`Send ${copy.name} message`}
              >
                {sending ? <SalesWizThinkingIcon className="scale-90" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function sales_wiz_tool_status(toolName: string): string {
  switch (toolName) {
    case "get_syndicated_report_profile":
      return "Checking report coverage"
    case "get_syndicated_brand_overview":
      return "Reading brand metrics"
    case "get_syndicated_brand_rankings":
      return "Ranking competitive brands"
    case "get_syndicated_product_drilldown":
      return "Inspecting SKU mix"
    case "get_syndicated_diagnostics":
      return "Diagnosing growth drivers"
    case "get_syndicated_raw_rows":
      return "Sampling source rows"
    case "search_retail_chains":
      return "Searching the chain catalog"
    case "get_retail_chain_profile":
      return "Reading the chain profile"
    default:
      return "Querying syndicated data"
  }
}

function is_status_placeholder(content: string): boolean {
  return content.endsWith("...") && !content.includes("\n")
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("visible_example_prompts", () => {
    it("removes prompts already used in the current chat", () => {
      const examples = REPORT_COPY.examples
      expect(visible_example_prompts(examples, [examples[0]])).toEqual(examples.slice(1))
    })
  })

  describe("panel_copy", () => {
    it("switches to chain copy when there is no report", () => {
      expect(panel_copy("report-1")).toBe(REPORT_COPY)
      expect(panel_copy()).toBe(CHAIN_COPY)
      expect(panel_copy("")).toBe(CHAIN_COPY)
    })

    it("gives each mode its own examples and empty state", () => {
      expect(panel_copy().emptyTitle).toBe("Ask about chains")
      expect(panel_copy().examples).not.toEqual(panel_copy("report-1").examples)
    })
  })

  describe("conversation_display_title", () => {
    it("uses the conversation title when present", () => {
      expect(conversation_display_title({ title: "What SKUs should I lead with?" })).toBe("What SKUs should I lead with?")
      expect(conversation_display_title({ title: "   " })).toBe("New SalesWiz chat")
    })

    it("falls back to the mode's own label", () => {
      expect(conversation_display_title({ title: null }, CHAIN_COPY.newChatTitle)).toBe("New chain chat")
    })
  })

  describe("sales_wiz_tool_status", () => {
    it("labels the chain catalog tools", () => {
      expect(sales_wiz_tool_status("search_retail_chains")).toBe("Searching the chain catalog")
      expect(sales_wiz_tool_status("get_retail_chain_profile")).toBe("Reading the chain profile")
    })
  })

  describe("escape_raw_html_for_sales_wiz_markdown", () => {
    it("preserves markdown formatting while escaping raw html", () => {
      const html = marked.parse(escape_raw_html_for_sales_wiz_markdown("**Safe** <img src=x onerror=alert(1)>")) as string
      expect(html).toContain("<strong>Safe</strong>")
      expect(html).toContain("&lt;img src=x onerror=alert(1)&gt;")
      expect(html).not.toContain("<img")
    })
  })
}
